import { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAction } from "convex/react";
import { api } from "../../../convex/_generated/api";
import { Card, CardHeader, CardTitle, CardContent, CardDescription } from "@/components/ui/Card";
import { CheckCircle2, AlertCircle, Loader2 } from "lucide-react";

export default function GoogleCallback() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState<"loading" | "success" | "error">("loading");
  const [errorMsg, setErrorMsg] = useState("");
  const calledRef = useRef(false);

  const exchangeToken = useAction(api.gdriveActions.exchangeToken);

  useEffect(() => {
    if (calledRef.current) return;

    const code = params.get("code");
    const error = params.get("error");
    const state = params.get("state");
    const expectedState = sessionStorage.getItem("gdrive_oauth_state");

    if (error) {
      calledRef.current = true;
      setStatus("error");
      setErrorMsg(params.get("error_description") || error);
      return;
    }

    if (!code) {
      calledRef.current = true;
      setStatus("error");
      setErrorMsg("Authorization code tidak ditemukan.");
      return;
    }

    if (expectedState && state && state !== expectedState) {
      calledRef.current = true;
      setStatus("error");
      setErrorMsg("Validasi sesi OAuth gagal (CSRF State Mismatch). Silakan ulangi login.");
      return;
    }
    sessionStorage.removeItem("gdrive_oauth_state");

    const userId = localStorage.getItem("pendingDriveUserId");
    const eventId = localStorage.getItem("pendingDriveEventId");

    if (!userId || !eventId) {
      calledRef.current = true;
      setStatus("error");
      setErrorMsg(
        "Sesi fotografer tidak ditemukan. Silakan kembali ke dashboard fotografer dan klik Hubungkan Drive lagi."
      );
      return;
    }

    calledRef.current = true;

    exchangeToken({ code, userId: userId as any, eventId: eventId as any })
      .then(() => {
        localStorage.removeItem("pendingDriveUserId");
        localStorage.removeItem("pendingDriveEventId");
        setStatus("success");
        setTimeout(() => navigate(`/photographer/${eventId}`), 2500);
      })
      .catch((err) => {
        setStatus("error");
        setErrorMsg(err.message || "Gagal menukar token.");
      });
  }, [params, exchangeToken, navigate]);

  return (
    <div className="min-h-screen flex items-center justify-center p-4 bg-slate-50">
      <Card className="max-w-md w-full text-center">
        <CardHeader>
          {status === "loading" && (
            <div className="flex flex-col items-center gap-2">
              <Loader2 className="w-10 h-10 text-blue-600 animate-spin" />
              <CardTitle>Menghubungkan Google Drive...</CardTitle>
              <CardDescription>Mohon tunggu sebentar.</CardDescription>
            </div>
          )}
          {status === "success" && (
            <div className="flex flex-col items-center gap-2">
              <div className="w-12 h-12 rounded-full bg-emerald-100 text-emerald-600 flex items-center justify-center">
                <CheckCircle2 className="w-6 h-6" />
              </div>
              <CardTitle>Berhasil Terhubung!</CardTitle>
              <CardDescription>Mengalihkan ke dashboard fotografer...</CardDescription>
            </div>
          )}
          {status === "error" && (
            <div className="flex flex-col items-center gap-2">
              <div className="w-12 h-12 rounded-full bg-red-100 text-red-600 flex items-center justify-center">
                <AlertCircle className="w-6 h-6" />
              </div>
              <CardTitle>Gagal Menghubungkan</CardTitle>
              <CardDescription className="text-red-600">{errorMsg}</CardDescription>
            </div>
          )}
        </CardHeader>
        {status === "error" && (
          <CardContent>
            <button
              onClick={() => navigate("/dashboard")}
              className="text-xs text-blue-600 hover:underline"
            >
              Kembali ke Dashboard
            </button>
          </CardContent>
        )}
      </Card>
    </div>
  );
}